import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { HistoryCard } from '@/components/billing/HistoryCard'
import { CurrentPlanCard } from '@/components/billing/PlanCard'
import { PurchaseDataCard } from '@/components/billing/PurchaseDataCard'
import { TrialOfferCard } from '@/components/billing/TrialOfferCard'
import { PageHeader } from '@/components/PageHeader'
import { capture } from '@/lib/analytics/posthog'
import {
  canSubscribe,
  isGatewayEnabled,
} from '@/lib/billing/gateways/availability'
import { createSubscriptionCheckout } from '@/lib/billing/server'
import { orgQueries } from '@/lib/org/queries'

type CheckoutStatus = 'success' | 'cancelled' | undefined

const asStatus = (v: unknown): CheckoutStatus =>
  v === 'success' || v === 'cancelled' ? v : undefined

export const Route = createFileRoute('/_authenticated/billing')({
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    purchase?: CheckoutStatus
    plan?: CheckoutStatus
    via?: 'crypto'
    subscribe?: string
    trial?: boolean
  } => ({
    purchase: asStatus(search.purchase),
    plan: asStatus(search.plan),
    // Which rail the customer just came back from. Only crypto sets it, and
    // only to change what we tell them about timing.
    via: search.via === 'crypto' ? 'crypto' : undefined,
    // Plan slug carried through signup from a marketing "Buy now" button —
    // auto-starts that plan's checkout on arrival.
    subscribe:
      typeof search.subscribe === 'string' &&
      /^[a-z0-9-]{1,32}$/.test(search.subscribe)
        ? search.subscribe
        : undefined,
    // The free-trial link (and the trial checkout's own return URLs).
    trial: search.trial === true || search.trial === 'true' ? true : undefined,
  }),
  // The plan card, purchase card and history all key off plan status, so it is
  // worth awaiting; without it the page renders a full set of empty cards first.
  loader: async ({ context: { queryClient, activeOrg } }) => {
    if (!activeOrg) return
    // See dashboard.tsx: the component is the error surface, not the route.
    await queryClient
      .ensureQueryData(orgQueries.planStatus(activeOrg.id))
      .catch(() => {})
  },
  component: BillingPage,
})

function BillingPage() {
  const { purchase, plan, via, subscribe, trial } = Route.useSearch()
  const { activeOrg } = Route.useRouteContext()
  const queryClient = useQueryClient()

  // Fulfil a marketing-site plan choice (?subscribe=<slug>): once the org and
  // plan status are known, go straight to that plan's Stripe checkout. Fires
  // at most once; an already-subscribed org just gets a pointer to the plan
  // card instead of a second checkout.
  const planStatus = useQuery({
    ...orgQueries.planStatus(activeOrg?.id ?? null),
    enabled: !!activeOrg,
  })
  const autoCheckout = useMutation({
    mutationFn: (planSlug: string) =>
      createSubscriptionCheckout({
        data: { planSlug, trial: trial === true },
      }),
    onSuccess: ({ url }) => {
      window.location.href = url
    },
    onError: (e) =>
      toast.error("Couldn't start checkout", {
        description: (e as Error).message,
      }),
  })
  const autoFired = useRef(false)
  useEffect(() => {
    if (!subscribe || autoFired.current) return
    // Back from a cancelled checkout: the offer card is on the page, and
    // forwarding again would loop them straight back to Stripe.
    if (plan === 'cancelled') return
    // The trial deep link lands on the offer card, which starts whichever
    // trial is on (granted on the spot, or at Stripe) from its own button.
    // Auto-forwarding here raced that button and minted two checkouts.
    if (trial) {
      autoFired.current = true
      return
    }
    if (!canSubscribe()) {
      autoFired.current = true
      toast('Contact sales', {
        description: 'Email sales@masklabs.io to purchase or change your plan.',
      })
      return
    }
    if (!activeOrg || !planStatus.data) return
    autoFired.current = true
    // A trialing org may still buy a real plan (the grant supersedes the
    // trial), so only a non-trial subscription is "already have a plan".
    if (planStatus.data.hasSubscription && !planStatus.data.trialing) {
      toast('You already have a plan', {
        description: 'Manage or change it from the plan card below.',
      })
      return
    }
    // More than one way to pay, so the customer picks rather than having card
    // chosen for them. The slug is handed to the plan card, which opens that
    // plan's payment step directly — a deep link must still land on a checkout,
    // not on a page of cards to search through.
    if (isGatewayEnabled('cryptomus')) {
      return
    }
    autoCheckout.mutate(subscribe)
  }, [subscribe, trial, plan, activeOrg, planStatus.data, autoCheckout.mutate])

  // Returning from a successful checkout: the webhook credits the wallet only
  // once Stripe confirms the charge. Card is near-instant, but async methods
  // (Cash App, Klarna) settle via `async_payment_succeeded` seconds-to-minutes
  // later — so poll the balance/history every few seconds for a couple of
  // minutes, not a fixed 8s, or the new total looks like it never arrived.
  useEffect(() => {
    if (purchase !== 'success' && plan !== 'success') return
    const refresh = () => {
      void queryClient.invalidateQueries({
        queryKey: ['org-balance', activeOrg?.id],
      })
      void queryClient.invalidateQueries({
        queryKey: ['org-ledger', activeOrg?.id],
      })
      void queryClient.invalidateQueries({
        queryKey: ['org-plan', activeOrg?.id],
      })
    }
    refresh()
    const interval = setInterval(refresh, 3000)
    // Crypto confirmations routinely outlast two minutes, and a page that
    // stops looking before the money lands shows a paid customer an unfunded
    // plan until they reload.
    const pollWindowMs = via === 'crypto' ? 600_000 : 120_000
    const stop = setTimeout(() => clearInterval(interval), pollWindowMs)
    return () => {
      clearInterval(interval)
      clearTimeout(stop)
    }
  }, [purchase, plan, via, activeOrg?.id, queryClient])

  // Surface the checkout-return outcome (from the Stripe redirect) as a one-off
  // toast rather than a persistent banner.
  useEffect(() => {
    if (purchase === 'success') {
      capture('data_topup_submitted')
      toast.success('Payment submitted', {
        description:
          'Your balance updates once the payment settles, usually instant for cards and a few minutes for Cash App or Klarna.',
      })
    } else if (purchase === 'cancelled') {
      toast('Checkout cancelled', { description: 'No charge was made.' })
    }
  }, [purchase])

  useEffect(() => {
    if (plan === 'success' && trial) {
      capture('checkout_returned', { kind: 'trial', via: 'card' })
      toast.success('Your free trial has started', {
        description:
          'Your trial data appears once the card is confirmed, usually within a few seconds.',
      })
    } else if (plan === 'success') {
      capture('checkout_returned', { kind: 'subscription', via: via ?? 'card' })
      // Crypto settles when the blockchain confirms it, which is minutes, not
      // seconds. Promising "a few seconds" there reads as a broken payment.
      toast.success(
        via === 'crypto' ? 'Payment sent' : 'Subscription started',
        {
          description:
            via === 'crypto'
              ? 'Your plan starts as soon as the network confirms the payment, usually a few minutes. You can close this page.'
              : 'Your plan data appears once the first invoice settles, usually within a few seconds.',
        },
      )
    } else if (plan === 'cancelled') {
      toast('Checkout cancelled', { description: 'No plan was started.' })
    }
  }, [plan, via, trial])

  const settling = purchase === 'success' || plan === 'success'

  return (
    <div className="flex flex-col gap-6">
      {/* The org name is already in the switcher AND the breadcrumb; spending
          the subtitle on it wastes the one slot that can explain the page. */}
      <PageHeader
        title="Billing"
        description="Your plan, data balance, and payment history."
      />

      {planStatus.data && !planStatus.data.hasSubscription ? (
        <TrialOfferCard orgId={activeOrg?.id} quiet={!(trial && subscribe)} />
      ) : null}

      <CurrentPlanCard
        orgId={activeOrg?.id}
        settling={settling}
        autoOpenSlug={trial ? undefined : subscribe}
      />

      <div className="grid gap-6 md:grid-cols-2">
        <PurchaseDataCard orgId={activeOrg?.id} />
      </div>

      {/* key by org so page state resets when the active org changes */}
      <HistoryCard
        key={activeOrg?.id}
        orgId={activeOrg?.id}
        orgName={activeOrg?.name}
      />
    </div>
  )
}
